import { useCallback } from 'react';

import { Apollo } from 'src/gql/generated';
import type { LabelSubset } from 'src/types';
import type { GroupWithLabels } from 'src/workflow/labels';

type DocumentWithGroupPermissions = NonNullable<
  Apollo.GetDocumentGroupPermissionsQueryHookResult['data']
>['document'];

export function useGetGroupsWithDocAccess(
  documentId: UUID | null,
  options?: Omit<
    NonNullable<Parameters<typeof Apollo.useGetDocumentGroupPermissionsQuery>[0]>,
    'variables'
  >
): {
  document: DocumentWithGroupPermissions | null;
  groupsWithAccess: DocumentWithGroupPermissions['groups'];
  loading: boolean;
} {
  const { data, loading } = Apollo.useGetDocumentGroupPermissionsQuery({
    ...options,
    skip: !documentId,
    variables: { documentId: documentId ?? '' },
  });
  return {
    document: data?.document ?? null,
    groupsWithAccess: data?.document.groups ?? [],
    loading,
  };
}

export function useSetGroupPermissions() {
  const [setGroupPermissions] = Apollo.useSetGroupPermissionsMutation();
  const safeSetGroupPermissions = useCallback(
    async (
      groupWithLabels: GroupWithLabels,
      options: { label: LabelSubset; type: 'add' | 'remove' }
    ) => {
      const { label, type } = options;
      const newLabels =
        type === 'add'
          ? [...groupWithLabels.labels, label]
          : groupWithLabels.labels.filter(l => l.id !== label.id);

      return setGroupPermissions({
        optimisticResponse: {
          __typename: 'Mutation',
          setGroupPermissions: {
            __typename: 'Group',
            id: groupWithLabels.id,
            labels: newLabels,
          },
        },
        variables: {
          input: {
            groupId: groupWithLabels.id,
            labelIds: newLabels.map(l => l.id),
          },
        },
      });
    },
    [setGroupPermissions]
  );

  return { setGroupPermissions: safeSetGroupPermissions };
}
